import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  Aangifte,
  BerichtenService, BokkzAlgDataService,
  BokkzClient,
  BokkzIbDataService, IbRekenService,
  IncomeTaxEntity, IntermediaryEntity,
  SamenvoegRequest,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {BehaviorSubject, filter, Observable, shareReplay, take} from 'rxjs';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {ButtonConfiguration, SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {CommonModule} from '@angular/common';
import {SharedUiLoadingModule} from '@snelstart/ui-loading';
import {ToastService} from "@snelstart/ui-toast";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-via',
  templateUrl: './inlezen.component.html',
  styleUrls: ['./inlezen.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    SharedUiButtonModule,
    SharedUiAlertModule,
    SharedUiLoadingModule,
    CommonModule
  ]
})
export class InlezenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private algDataService = inject(BokkzAlgDataService);
  private destroyRef = inject(DestroyRef);
  private bokkzClient = inject(BokkzClient);
  private apiService = inject(IbRekenService);
  private berichtenService = inject(BerichtenService);
  private toastService = inject(ToastService);
  public checkPartner$ = new BehaviorSubject<boolean>(false);
  public checkVorigJaar$ = new BehaviorSubject<boolean>(false);

  readonly intermediaryEntity$: Observable<IntermediaryEntity> =
    this.algDataService.intermediaryEntity$.pipe(
      filter((data): data is IntermediaryEntity => !!data?.sstPrimaryAdminId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public sstRelationId: any;
  public incomeTaxEntity = {} as IncomeTaxEntity;
  public incomeTaxEntityPartner = {} as IncomeTaxEntity;
  public incomeTaxEntityVorigJaar = {} as IncomeTaxEntity;
  public selectedYear: any;
  public actualYear = new Date().getFullYear();
  public heeftVia = false;
  public heeftViaPartner = false;
  public heeftVorigJaar = false;
  public viaBeschikbaar = false;
  public viaJson = {} as Aangifte;
  public viaJsonPartner = {} as Aangifte;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'}
  ];

  ngOnInit(): void {
    this.intermediaryEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.incomeTaxEntity = value;
      this.selectedYear = value.year;
      let date = (value.year! + 1) + "-03-01"
      this.viaBeschikbaar = new Date(date) < new Date(new Date().toDateString())
      if (value.viaXbrl) {
        this.heeftVia = true;
        this.getViaJson(value.viaXbrl, false);
      } else if (value.via) {
        this.heeftVia = true;
        this.viaJson = value.via;
      }
      if (value.aangifte?.partnerAangifte?.aangever?.bsn) {
        this.bokkzClient.getIncomeTax(value.aangifte?.partnerAangifte?.aangever?.bsn, String(value.year))
          .pipe(
            take(1),
            takeUntilDestroyed(this.destroyRef)
          ).subscribe(value => {
          if (value.id) {
            this.incomeTaxEntityPartner = value;
            if (value.viaXbrl) {
              this.heeftViaPartner = true;
              this.getViaJson(value.viaXbrl, true);
            } else if (value.via) {
              this.heeftViaPartner = true;
              this.viaJson = value.via;
            }
            this.checkPartner$.next(true)
          }
        })
      }
      this.bokkzClient.getIncomeTax(value.aangifte?.aangever?.bsn!, String(value.year! -1))
        .pipe(
          take(1),
          takeUntilDestroyed(this.destroyRef),
        ).subscribe(data => {
          if (data.aangifte !== undefined) {
            this.heeftVorigJaar = true;
            this.incomeTaxEntityVorigJaar = data;
            this.checkVorigJaar$.next(true)
          }
      })
    });
  }

  ngOnDestroy(): void {
    // nothing to destroy
  }

  public configAangever = ButtonConfiguration.create({
    onClick: () =>
      new Observable((subscription) => {
        setTimeout(() => {
          subscription.next(true);
          subscription.complete();
          this.updateAangifteMetVia(false);
        }, 500);
      })
  });

  public configPartner = ButtonConfiguration.create({
    onClick: () =>
      new Observable((subscription) => {
        setTimeout(() => {
          subscription.next(true);
          subscription.complete();
          this.updateAangifteMetVia(true);
        }, 500);
      })
  });

  public configUpdate = ButtonConfiguration.create({
    onClick: () =>
      new Observable((subscription) => {
        setTimeout(() => {
          subscription.next(true);
          subscription.complete();
          this.updateAangifteMetGegevensVorigJaar();
        }, 500);
      })
  });

  public getViaJson(viaXbrlString: string, partner: boolean) {
    const ophaalResponse = {
      inhoud: btoa(viaXbrlString),
      berichtsoort: 'VIA'
    };
    this.berichtenService.converteerXbrlToJson(ophaalResponse).subscribe((data) => {
      if (partner) {
        this.viaJsonPartner = data;
      } else this.viaJson = data;
    });
  }

  public updateAangifteMetVia(partner: boolean) {
    const samenvoegRequest = {
      viaAangever: this.viaJson,
      viaPartner: partner ? this.viaJsonPartner : null,
      aangifte: this.incomeTaxEntity.aangifte
    } as SamenvoegRequest

    this.bokkzClient.getSamenvoegResponse(samenvoegRequest).subscribe((data) => {
      if (data.aangifteJaar) {
        this.incomeTaxEntity = {
          ...this.incomeTaxEntity,
          aangifte: data,
        }
        this.toastService.showSuccessMessage("De vooraf ingevulde gegevens zijn ingelezen in de aangifte.")
        this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
      } else {
        this.toastService.showErrorMessage("Er is een fout opgetreden bij het inlezen van de VIA gegevens. Probeer het later nog eens of neem contact op met onze klantenservice.")
      }
    })
  }

  public updateAangifteMetGegevensVorigJaar() {
    this.apiService.getIncomeTaxVoorspelling(this.incomeTaxEntityVorigJaar.aangifte!, this.selectedYear).pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((data) => {
      this.dataService.patchIncomeTaxEntity((entity) => ({
        ...entity,
        aangifte: data,
      }));
      this.dataService.calculateLatestIncomeTaxReturn().subscribe();
    })
  }
}
